import { assertBuildAllowed, assertSellBuildingAllowed, groupHasBuildings, liquidationCapacity, ownsCompleteGroup } from '../engine/rules.js';
import { GameError } from '../engine/GameError.js';
import { botWantsTrade, proposeBotTrade } from './BotTrading.js';

const RESERVE = 220;
const choice = (player, method, ...args) => ({ player, method, args });
const legal = (check) => {
  try { check(); return true; } catch (error) { if (!(error instanceof GameError)) throw error; return false; }
};

// Decisions contain IDs only. All mutations and rule checks remain in GameEngine.
export function chooseBotAction(game) {
  if (game.status !== 'active') return null;
  const player = game.phase === 'awaiting_debt'
    ? game.getPlayer(game.pendingDebt?.debtorId)
    : game.currentPlayer;
  if (game.phase !== 'awaiting_debt') {
    const incoming = game.trades.find((trade) => trade.status === 'pending' && game.getPlayer(trade.toPlayerId)?.isBot);
    if (incoming) {
      const recipient = game.getPlayer(incoming.toPlayerId);
      return choice(recipient, botWantsTrade(game, recipient, incoming) ? 'acceptTrade' : 'declineTrade', incoming.id);
    }
  }
  if (!player?.isBot || player.bankrupt) return null;
  const owned = player.properties.map((id) => game.getProperty(id));
  if (game.phase === 'awaiting_debt') {
    if (player.money >= game.pendingDebt.amount) return choice(player, 'resolveDebt');
    if (liquidationCapacity(game, player.id) < game.pendingDebt.amount) return choice(player, 'declareBankruptcy');
    const building = owned.find((property) => legal(() => assertSellBuildingAllowed(game, player.id, property.id)));
    if (building) return choice(player, 'sellBuilding', building.id);
    const mortgage = owned.find((property) => !property.mortgaged && !groupHasBuildings(game, game.board[property.id].group));
    return mortgage ? choice(player, 'mortgage', mortgage.id) : null;
  }
  if (game.phase === 'awaiting_purchase') {
    const cell = game.board[game.pendingPurchase.propertyId];
    const completesGroup = cell.group && game.board.filter((item) => item.group === cell.group && item.index !== cell.index)
      .every((item) => game.getProperty(item.index)?.ownerId === player.id);
    const reserve = cell.type === 'transport' || completesGroup ? 120 : RESERVE;
    return choice(player, player.money - cell.price >= reserve ? 'buyProperty' : 'declineProperty');
  }
  if (game.phase === 'awaiting_roll') {
    if (player.jail.inJail && player.jailFreeCards > 0) return choice(player, 'useJailCard');
    if (player.jail.inJail && player.jail.turns >= 2 && player.money - game.settings.jailFine >= RESERVE) return choice(player, 'payJailFine');
    return choice(player, 'rollDice');
  }
  if (game.phase === 'awaiting_end') {
    const redeem = owned.find((property) => property.mortgaged && player.money - Math.ceil(game.board[property.id].mortgageValue * 1.1) >= RESERVE + 100);
    if (redeem) return choice(player, 'unmortgage', redeem.id);
    const building = owned.find((property) => ownsCompleteGroup(game, player.id, game.board[property.id].group)
      && player.money - game.board[property.id].buildCost >= RESERVE + 100
      && legal(() => assertBuildAllowed(game, player.id, property.id)));
    if (building) return choice(player, 'build', building.id);
    const actionId = `bot-trade:${game.dice.sequence}`;
    if (!game.findProcessedAction(player.id, actionId)) {
      const proposal = proposeBotTrade(game, player);
      if (proposal) return { ...choice(player, 'createTrade', proposal), actionId };
    }
    return choice(player, 'endTurn');
  }
  return null;
}
